import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'

import { CurrencyCodeEnum } from '@/enums'
import { getFormatNumber } from '@/utils'
import { dateTimeTableFormat } from '@/constants/date'

import { balanceReportSelector } from '@/state/balance-report/slices/index.slices'
import {
  BalanceReportData,
  BalanceReportLibraryColumnProps,
} from '@/state/balance-report/models'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

const useBalanceReportTable = () => {
  const { t } = useTranslation()

  const { reports, pagination, isLoading } = useSelector(
    balanceReportSelector,
    shallowEqual,
  )

  const headCells: TableHeadCellPropsType<BalanceReportLibraryColumnProps>[] = [
    { id: 'playerId', label: t('labels.playerId') },
    { id: 'username', label: t('labels.username') },
    { id: 'balance', label: t('labels.balance') },
    { id: 'lastDayPlayed', label: t('reports.lastDayPlayed') },
    { id: 'lifetimeWL', label: t('reports.lifetimeWL') },
  ]

  const headXlsxCells: TableHeadCellPropsType<BalanceReportLibraryColumnProps>[] =
    [
      { id: 'playerId', label: t('labels.playerId') },
      { id: 'username', label: t('labels.username') },
      { id: 'balance', label: t('labels.balance') },
      { id: 'lastDayPlayed', label: t('reports.lastDayPlayed') },
      { id: 'lifetimeBet', label: t('reports.bet') },
      { id: 'lifetimeWin', label: t('reports.win') },
      { id: 'lifetimeWL', label: t('reports.lifetimeWL') },
      { id: 'currency', label: t('labels.currency') },
    ]

  const getFormattedRow = (
    report: BalanceReportData,
  ): BalanceReportLibraryColumnProps => {
    const currency = report.currency || CurrencyCodeEnum.USD
    const lastPlayed = report.lastDayPlayed
      ? new Date(report.lastDayPlayed)
      : null

    return {
      ...report,
      balanceText: getFormatNumber(Number(report.balance), currency),
      lifetimeWLText: getFormatNumber(Number(report.lifetimeWL), currency),
      lastDayPlayedText:
        lastPlayed && !isNaN(lastPlayed.getTime())
          ? format(lastPlayed, dateTimeTableFormat)
          : '-',
    }
  }

  const formattedReports =
    reports?.map((report) => getFormattedRow(report)) || []

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    BalanceReportLibraryColumnProps,
    keyof BalanceReportLibraryColumnProps
  >(formattedReports, 'balance', 'desc')

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    pagination,
    isLoading,
    headCells,
    headXlsxCells,
    getFormattedRow,
  }
}

export default useBalanceReportTable
